import { CopilotChannelContext } from 'copilot-toolkit/channel/CopilotChannelContext';
import { capabilities } from 'navigation-components/capabilities';
import { chatspotSidebarComponentDef } from 'navigation-components/config';
import { useInvokeCapability, useOnFunctionCalled } from 'navigation-components/consumer/hooks';
import { useSendCopilotAction } from 'copilot-toolkit/hooks/useSendCopilotAction';
import { Suspense, lazy, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { chatInputChanged } from '../chat/slices/chat';
import { chatV2Actions } from '../chat/slices/chatv2';
import { setCopilotQueryParams } from '../chat/slices/parentApp';
import { useIsUngatedForCopilotV2 } from '../gating/hooks/useIsUngatedForCopilotV2';
import { useHostAppContext } from '../hostAppContext/hooks/useAppContext';
import { draftParameterValueChanged, promptTemplateSelected } from '../promptTemplates/slices/promptTemplates';
import { getDraftPromptTemplateFromSuperstore } from '../superstore/utils/promptTemplate';
import { getDraftMessageFromSuperstore } from '../superstore/utils/userDraftMessage';
import { useIsDisabledPath } from '../threads/hooks/useOnParentPathnameChange';
import { usageTracker } from '../utils/usageTracker';
import AlwaysOnNav from './AlwaysOnNav';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AsyncNavigationVariant = /*#__PURE__*/lazy(() => import('./NavigationVariant'));
const NavigationComponentSetup = props => {
  const isPathDisabled = useIsDisabledPath();
  const {
    copilotLayoutType,
    isSearchOpen,
    state,
    threadIdQueryParam
  } = props;
  const sendCopilotAction = useSendCopilotAction();
  const safeCopilotLayoutType = useMemo(() => {
    if (copilotLayoutType === 'panel-with-scroll' || copilotLayoutType === 'panel' || copilotLayoutType === 'sidebar') {
      return copilotLayoutType;
    }

    // We don't want to default this, if we did it would override the legacy
    // setup (via CopilotChannel). Eventually when all apps are using Quartz
    // Framework we will enforce an invariant that this will always be set.
    return undefined;
  }, [copilotLayoutType]);
  const isOpen = !isPathDisabled && state.isOpen;
  const dispatch = useDispatch();
  const [shouldRenderApp, setShouldRenderApp] = useState(false);
  const {
    channel
  } = useContext(CopilotChannelContext);
  const {
    setIsCopilotExpanded,
    setIsGlobalSearchOpen
  } = useHostAppContext();
  const {
    registerCallback
  } = useOnFunctionCalled(chatspotSidebarComponentDef);
  const updateQueryParam = useInvokeCapability(capabilities.UPDATE_QUERY_PARAM_CAP);
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  useEffect(() => {
    if (isOpen) {
      setShouldRenderApp(true);
    }
  }, [isOpen]);
  useEffect(() => {
    const initializeState = async () => {
      if (state.isReady) {
        const draftMessage = await getDraftMessageFromSuperstore();
        const draftPromptTemplate = await getDraftPromptTemplateFromSuperstore();
        if (draftPromptTemplate) {
          dispatch(promptTemplateSelected({
            templateId: draftPromptTemplate.templateId
          }));
          if (draftPromptTemplate.draftValues) {
            dispatch(draftParameterValueChanged({
              values: Object.assign({}, draftPromptTemplate.draftValues)
            }));
          }
        }
        if (isUngatedForCopilotV2) {
          dispatch(chatV2Actions.chatInputChanged(draftMessage));
        }
        dispatch(chatInputChanged(draftMessage));
        dispatch(setCopilotQueryParams({
          copilotThreadId: threadIdQueryParam
        }));
        updateQueryParam({
          value: '',
          key: 'copilotThreadId'
        });
      }
    };
    void initializeState();
  }, [dispatch, state, threadIdQueryParam, updateQueryParam, isUngatedForCopilotV2]);
  useEffect(() => {
    setIsGlobalSearchOpen(!!isSearchOpen);
    if (isSearchOpen) {
      setIsCopilotExpanded(false, {
        source: 'global-search-open'
      });
    }
  }, [isSearchOpen, setIsCopilotExpanded, setIsGlobalSearchOpen]);
  useEffect(() => {
    if (channel && !isPathDisabled) {
      channel.setState({
        isNavCopilotAvailable: true
      });
    }
    return () => {
      if (channel && !isPathDisabled) {
        channel.setState({
          isNavCopilotAvailable: false
        });
      }
    };
  }, [channel, isPathDisabled]);

  // We manage our own event tracking from navigation-components
  useEffect(() => {
    return registerCallback('onCopilotToggled', ({
      source
    }) => {
      // isOpen is coming from the nav component, which represents
      // the state we are transitioning to when Copilot is toggled.
      usageTracker.track('chatspotCoreInteraction', {
        action: 'toggle_copilot_panel',
        intent: isOpen ? 'open' : 'close',
        source
      });
    });
  }, [registerCallback, isOpen]);

  // This is to register an invoke function to send actions from navigation
  useEffect(() => {
    return registerCallback('onBreezeAssistantSidebarAction', ({
      actionData
    }) => {
      sendCopilotAction({
        messageAction: actionData
      });
      usageTracker.track('chatspotCoreInteraction', {
        action: 'breeze_assistant_action',
        source: 'ACTION'
      });
    });
  }, [registerCallback, sendCopilotAction]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(AlwaysOnNav, {
      copilotLayoutType: safeCopilotLayoutType,
      isOpen: isOpen,
      isSearchOpen: isSearchOpen
    }), shouldRenderApp && /*#__PURE__*/_jsx(Suspense, {
      fallback: null,
      children: /*#__PURE__*/_jsx(AsyncNavigationVariant, {
        isOpen: isOpen
      })
    })]
  });
};
export default NavigationComponentSetup;